import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Save, Check } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

const Step3AssetCategories = ({ data, onNext, onPrevious, onSaveAndExit, saving }) => {
  const [selectedCategories, setSelectedCategories] = useState(data.selected_asset_categories || []);

  const categories = [
    { id: 'immovable', label: 'Immovable Property', description: 'Land, houses, flats, commercial property' },
    { id: 'bank_accounts', label: 'Bank Accounts', description: 'Savings, current, fixed deposits' },
    { id: 'insurance', label: 'Insurance Policies', description: 'Life, health, vehicle insurance' },
    { id: 'mutual_funds', label: 'Mutual Funds', description: 'Equity, debt, hybrid funds' },
    { id: 'shares', label: 'Shares & Securities', description: 'DEMAT account, shares, bonds' },
    { id: 'pf_pension', label: 'Provident & Pension Funds', description: 'EPF, PPF, NPS' },
    { id: 'business', label: 'Business Interests', description: 'Company shares, partnerships' },
    { id: 'esop_rsu', label: 'ESOPs / RSUs', description: 'Employee stock options' },
    { id: 'digital_assets', label: 'Digital Assets', description: 'Crypto, NFTs, domains' },
    { id: 'vehicles', label: 'Vehicles', description: 'Cars, bikes, other vehicles' },
    { id: 'jewellery', label: 'Jewellery & Valuables', description: 'Gold, silver, diamond, artwork' },
    { id: 'intellectual_property', label: 'Intellectual Property', description: 'Patents, copyrights, trademarks' },
    { id: 'locker', label: 'Safety Lockers', description: 'Bank lockers' },
    { id: 'foreign_assets', label: 'Foreign Assets', description: 'Assets held in other countries' }
  ];

  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedCategories.length === 0) {
      toast.error('Please select at least one asset category');
      return;
    }
    onNext({ selected_asset_categories: selectedCategories });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8" data-testid="step3-form">
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white border-b border-slate-700 pb-2">Select Your Asset Categories</h3>
        <p className="text-sm text-slate-400">Choose all that apply. You'll provide details in the next step.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((category) => (
            <div
              key={category.id}
              onClick={() => toggleCategory(category.id)}
              className={`p-4 rounded-xl cursor-pointer transition-all border-2 ${
                selectedCategories.includes(category.id)
                  ? 'bg-amber-500/10 border-amber-500'
                  : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
              }`}
              data-testid={`category-${category.id}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  selectedCategories.includes(category.id)
                    ? 'bg-amber-500 text-slate-900'
                    : 'bg-slate-700 text-transparent'
                }`}>
                  <Check size={14} />
                </div>
                <div>
                  <h4 className={`font-medium ${
                    selectedCategories.includes(category.id) ? 'text-amber-400' : 'text-white'
                  }`}>{category.label}</h4>
                  <p className="text-sm text-slate-500 mt-1">{category.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {selectedCategories.length > 0 && (
          <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
            <p className="text-sm text-slate-300">
              <span className="text-amber-400 font-semibold">{selectedCategories.length}</span> categories selected
            </p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex flex-wrap justify-between gap-4 pt-6 border-t border-slate-700">
        <div className="flex gap-2">
          <Button type="button" onClick={onPrevious} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
            <ChevronLeft size={16} className="mr-2" /> Previous
          </Button>
          <Button type="button" onClick={() => onSaveAndExit({ selected_asset_categories: selectedCategories })} disabled={saving} variant="outline" className="border-emerald-600 text-emerald-400 hover:bg-emerald-500/10">
            <Save size={16} className="mr-2" /> Save & Exit
          </Button>
        </div>
        <Button type="submit" disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold">
          Next: Define Assets <ChevronRight size={16} className="ml-2" />
        </Button>
      </div>
    </form>
  );
};

export default Step3AssetCategories;
